import React from "react";
import { AlertTriangle } from "lucide-react";
import { ACTIVITY_COLORS, ACTIVITY_LABELS } from "@/lib/api";
import { colorOf } from "@/lib/timesheetUtils";

const ACTIVITIES = ["SICK", "ABSENCE", "ONBOARDING", "TRAINING", "CONGE"];
const GESTIONNAIRE_ACTIVITIES = ["DAILY", "SICK", "ABSENCE", "TRAINING", "CONGE"];
// Iter-63: freelance collaborators only see Leave (CONGE) plus their missions.
const FREELANCE_ACTIVITIES = ["CONGE"];

/**
 * Sélecteur d'activité + chips missions.
 *
 * @param {Object} props.user
 * @param {string} props.selectedActivity
 * @param {(a:string)=>void} props.setSelectedActivity
 * @param {string} props.selectedMission
 * @param {(id:string)=>void} props.setSelectedMission
 * @param {Array} props.visibleMissions
 * @param {Object} props.missionMonthDays  Days-this-month per mission_id
 */
export default function SelectorBar({
  user,
  selectedActivity,
  setSelectedActivity,
  selectedMission,
  setSelectedMission,
  visibleMissions,
  missionMonthDays,
}) {
  const activityList = user.role === "GESTIONNAIRE"
    ? GESTIONNAIRE_ACTIVITIES
    : (user.is_freelance ? FREELANCE_ACTIVITIES : ACTIVITIES);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6 flex flex-wrap items-center gap-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 pr-2 font-mono">Selector</div>

      {user.role !== "GESTIONNAIRE" && (
        <button
          onClick={() => setSelectedActivity("MISSION")}
          data-testid="sel-activity-MISSION"
          className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${
            selectedActivity === "MISSION"
              ? "bg-zinc-950 text-white"
              : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
          }`}
        >
          Mission
        </button>
      )}

      {activityList.map((a) => (
        <button
          key={a}
          onClick={() => setSelectedActivity(a)}
          data-testid={`sel-activity-${a}`}
          className={`px-3 h-8 rounded-md text-xs font-medium transition-colors inline-flex items-center gap-2 ${
            selectedActivity === a ? "text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
          }`}
          style={selectedActivity === a ? { background: ACTIVITY_COLORS[a] || "#355278" } : {}}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{
              background: selectedActivity === a ? "#fff" : (ACTIVITY_COLORS[a] || "#355278"),
            }}
          />
          {/* Iter-65: freelance profiles see "Leave" instead of "Paid leave". */}
          {a === "CONGE" && user.is_freelance ? "Leave" : (ACTIVITY_LABELS[a] || a)}
        </button>
      ))}

      {selectedActivity === "MISSION" && user.role !== "GESTIONNAIRE" && (
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {visibleMissions.length === 0 && (
            <span className="text-xs text-zinc-500">No mission assigned for this month yet.</span>
          )}
          {visibleMissions.map((m) => {
            const env = Number(m.envelope_jh || 0);
            const cons = Number(m.consumed_jh || 0);
            const monthD = Number(missionMonthDays[m.mission_id] || 0);
            const ratio = env > 0 ? cons / env : 0;
            const isOver = env > 0 && cons > env + 1e-9;
            const isWarn = env > 0 && !isOver && ratio >= 0.8;
            const isSelected = selectedMission === m.mission_id;
            const remaining = env > 0 ? Math.max(0, env - cons) : null;
            // Iter-215 — display "remaining/envelope" instead of "consumed/envelope".
            // When over budget, remaining is clamped to 0 and the red tone highlights it.
            const counterText = env > 0
              ? `${(remaining ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}/${env.toLocaleString(undefined, { maximumFractionDigits: 2 })} j`
              : null;
            const counterTone = isSelected
              ? "bg-white/25 text-white"
              : isOver
                ? "bg-red-100 text-red-800"
                : isWarn
                  ? "bg-amber-100 text-amber-800"
                  : "bg-emerald-100 text-emerald-800";
            const monthTone = isSelected ? "bg-white/25 text-white" : "bg-zinc-200 text-zinc-700";
            return (
              <button
                key={m.mission_id}
                onClick={() => setSelectedMission(m.mission_id)}
                data-testid={`sel-mission-${m.mission_id}`}
                className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-2 transition-all ${
                  isSelected ? "text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                } ${isOver && !isSelected ? "ring-1 ring-red-400" : ""}`}
                style={isSelected ? { background: colorOf(m) } : {}}
                title={counterText
                  ? `Mois courant: ${monthD.toLocaleString(undefined, { maximumFractionDigits: 2 })} j · Enveloppe ${env} j · Consommé ${cons} j · Reste ${remaining} j${isOver ? " — DÉPASSEMENT" : ""}`
                  : `Mois courant: ${monthD.toLocaleString(undefined, { maximumFractionDigits: 2 })} j`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: isSelected ? "#fff" : colorOf(m) }}
                />
                <span className="truncate max-w-[200px]">{m.title}</span>
                {/* Iter-79: month-to-date days, increments on every click */}
                <span
                  data-testid={`mission-month-counter-${m.mission_id}`}
                  className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${monthTone}`}
                >
                  {monthD.toLocaleString(undefined, { maximumFractionDigits: 2 })} j
                </span>
                {counterText && (
                  <span
                    data-testid={`mission-envelope-counter-${m.mission_id}`}
                    className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-mono inline-flex items-center gap-1 ${counterTone}`}
                  >
                    {isOver && <AlertTriangle size={10} />} {counterText}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
